/**
 * Every sentence of the applicant journey that is not in the catalogue, in one
 * place, so a second language is one file (SPEC, Non-functional).
 */
export const COPY = {
  landingTitle: 'Licensing intake for remittance businesses',
  landingIntro:
    'The Central Bank of Kenya, the Bank of Uganda and the Bank of Tanzania each license remittance separately. ' +
    'This questionnaire gathers what all three ask, so we can advise on and prepare your applications.',
  landingEstimate: (sections: number) =>
    `It takes 15 to 20 minutes, in ${sections} sections. Your answers are saved as you type, so you can stop and come back on any device.`,
  privacyTitle: 'How we use what you tell us',
  privacyText:
    'You will be asked about shareholders, directors and owners, including passport details, politically exposed persons ' +
    'and any convictions. Only you and our advisory team can see your answers. We use them to assess and prepare your ' +
    'licence applications, keep them for as long as we are required to, and delete them when you ask us to, unless the ' +
    'law requires otherwise.',
  consentLabel:
    'I am authorised to share the personal information of the shareholders, directors, beneficial owners and officers named in this application.',
  consentRequired: 'Confirm this before starting.',
  start: 'Start',
  continue: 'Continue where I left off',
  startOver: 'Start over',
  startOverConfirm:
    'Start over? Every answer in this application will be discarded. This cannot be undone.',
  sectionEyebrow: (n: number, of: number) => `Section ${n} of ${of}`,
  back: 'Back',
  next: 'Next',
  toDocuments: 'Attach documents',
  toReview: 'Review',
  required: 'Required',
  optional: 'Optional',
  yes: 'Yes',
  no: 'No',
  chooseOne: 'Choose one',
  documentsTitle: 'Supporting documents',
  documentsIntro: 'These are the documents the central banks expect.',
  documentsRelevant: 'Relevant to your answers',
  documentsLater: 'Anything you do not have yet can follow later.',
  documentsLimits: 'PDF, JPG, PNG, HEIC, DOCX or XLSX, up to 20 MB each.',
  attach: 'Attach file(s)',
  attachTo: (title: string) => `Attach file(s) to ${title}`,
  remove: 'Remove',
  removeFile: (name: string) => `Remove ${name}`,
  uploading: (percent: number) => `Uploading, ${percent}%`,
  scan: {
    PENDING: 'Checking for malware',
    CLEAN: 'Ready',
    INFECTED: 'Malware found. This file will not be sent; remove it and attach a clean copy.',
    FAILED: 'Could not be checked. The advisory team will follow up.',
  },
  notAttached: 'Could not be attached',
  documentsUnavailable: 'Your documents could not be loaded. Try again in a moment.',
  reviewTitle: 'Review your answers',
  notAnswered: 'Not answered',
  skipped: 'Skipped (optional)',
  notProvided: 'Not provided',
  /** `when`: the submission mode while it may still be sent; `sent` once it cannot. */
  incomplete: (n: number, when: 'lenient' | 'strict' | 'sent') => {
    if (n === 0) return 'Every section is complete.';
    const count = `${n} ${n === 1 ? 'section is' : 'sections are'} incomplete.`;
    if (when === 'lenient') return `${count} You can still send it, and add what is missing later.`;
    if (when === 'strict') return `${count} Complete every required question before you send it.`;
    return count;
  },
  submit: 'Send to the advisory team',
  sending: 'Sending',
  sendAgain: 'Send again',
  unsavedBeforeSend: 'Your latest changes have not been saved yet, so the application was not sent. Try again in a moment.',
  sendFailedTitle: 'Not sent',
  sendFailed: 'The application could not be sent. Try again in a moment.',
  sentTitle: 'Your application has been sent',
  sentReference: 'Reference',
  sentOn: 'Sent',
  sentStatus: 'Status',
  sentConfirmation: 'A confirmation has been emailed to you. Quote the reference whenever you write to the team.',
  whatNext: 'What happens next',
  whatNextText:
    'The advisory team will review your application and write to you. If they need anything more, it will open again here for you to add it, and you will be emailed to say what they need.',
  sentWithGaps: (n: number) =>
    `Sent with ${n} ${n === 1 ? 'section' : 'sections'} incomplete. The team may ask you for what is missing:`,
  viewSent: 'View what you sent',
  backToConfirmation: 'Back to confirmation',
  statusNames: {
    DRAFT: 'Draft',
    SUBMITTED: 'Received, awaiting review',
    IN_REVIEW: 'In review',
    INFORMATION_REQUESTED: 'More information requested',
    CLOSED: 'Closed',
  },
  yourData: 'Your data',
  yourDataText:
    'Your answers and documents are seen only by you and the advisory team, and every look the team takes is recorded. Signing out ends your session on every device.',
  askDeletion: 'Ask for your data to be deleted',
  askDeletionConfirm:
    'The advisory team will be asked to delete your data, and will reply by email. A submitted application may have to be kept for a time; they will tell you.',
  sendDeletion: 'Send the request',
  keepData: 'Do not send',
  deletionSent: 'Your request has been sent to the advisory team, who will reply by email.',
  deletionFailed: 'The request could not be sent. Try again in a moment.',
  infoRequestedTitle: 'The advisory team has asked for more information',
  infoRequestedOn: (date: string) => `Asked on ${date}. Add what they need, then send the application again from the review step.`,
  stillToComplete: 'Still to complete:',
  goTo: (n: number, title: string) => `${n}. ${title}`,
  stampName: (n: number, title: string) => `Section ${n}: ${title}`,
  keepCopy: 'Keep a copy',
  keepCopyIntro:
    'A summary of every answer and document listed here, to keep or to share with your colleagues. It holds personal details; store it with care.',
  downloadPdf: 'Download PDF',
  downloadText: 'Download text',
  copySummary: 'Copy',
  preparing: 'Preparing the summary',
  copied: 'The summary is on your clipboard.',
  summaryFailed: 'The summary could not be prepared. Try again in a moment.',
  copyFailed: 'The summary could not be copied here. Download it instead.',
  clearEverything: 'Clear everything',
  readOnly: 'This application has been submitted, so its answers can no longer be changed here.',
  stamps: 'Steps',
  saving: 'Saving',
} as const;
